/**
 * Sidebar-Navigation als reine Datenstruktur. Hier wird die gesamte
 * Information-Architecture des ERP definiert — Module, Untergruppen,
 * Stub-Items mit Phase-Zuordnung.
 *
 * Routing-Hinweis: Die `to`-Felder zeigen auf die aktuell registrierten
 * Routen. Items ohne `to` sind Stubs (kommen mit ihrer jeweiligen Phase)
 * und werden in der Sidebar grau, nicht klickbar dargestellt. Sobald ein
 * Modul live geht, wird hier nur das `to` ergänzt — keine Strukturänderung
 * ist nötig.
 */
export interface NavLeaf {
  kind: 'item';
  label: string;
  /** Wenn unset: Stub, in Sidebar grau + non-clickable. */
  to?: string;
  /** Hinweis-Text fuer Stubs (Tooltip / Hover). */
  hint?: string;
  /** Optionale Active-Match-Pfade — wenn der aktuelle Pfad einer dieser Prefixe ist, gilt das Item als aktiv. Default: nur exakter Match auf `to`. */
  matchPrefixes?: string[];
}

export interface NavSubgroup {
  kind: 'group';
  label: string;
  items: NavLeaf[];
}

export type NavChild = NavLeaf | NavSubgroup;

export interface NavSection {
  /** Top-level-Bereich. */
  label: string;
  /** Wenn gesetzt: Section ist ein direkter Link (Dashboard) ohne Children. */
  to?: string;
  children?: NavChild[];
  /** Stable-Id fuer localStorage-Persistierung des Open/Closed-Zustands. */
  id: string;
}

export const NAV_SECTIONS: NavSection[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    to: '/',
  },
  {
    id: 'inbox',
    label: 'Inbox',
    children: [
      { kind: 'item', label: 'Eingang', to: '/inbox', matchPrefixes: ['/inbox'] },
      { kind: 'item', label: 'Aufgaben & Wiedervorlagen', hint: 'Phase 4+' },
    ],
  },
  {
    id: 'stammdaten',
    label: 'Stammdaten',
    children: [
      { kind: 'item', label: 'Kunden', to: '/kunden', matchPrefixes: ['/kunden'] },
      { kind: 'item', label: 'Objekte', to: '/objekte', matchPrefixes: ['/objekte'] },
      { kind: 'item', label: 'Mitarbeiter', to: '/mitarbeiter', matchPrefixes: ['/mitarbeiter'] },
      {
        kind: 'item',
        label: 'Subunternehmer',
        to: '/subunternehmer',
        matchPrefixes: ['/subunternehmer'],
      },
      { kind: 'item', label: 'Lieferanten', hint: 'Phase 5+' },
      { kind: 'item', label: 'Kontakte', to: '/kontakte', matchPrefixes: ['/kontakte'] },
      {
        kind: 'item',
        label: 'Artikel & Leistungskatalog',
        to: '/artikel',
        matchPrefixes: ['/artikel'],
      },
      { kind: 'item', label: 'LV-Vorlagen', hint: 'Phase 5+' },
    ],
  },
  {
    id: 'vertraege',
    label: 'Verträge',
    children: [
      {
        kind: 'item',
        label: 'Betreuungsverträge',
        to: '/vertraege',
        matchPrefixes: ['/vertraege/bv', '/vertraege/entwurf', '/vertraege/neu'],
      },
      {
        kind: 'item',
        label: 'Mahnwesen',
        to: '/vertraege/mahnungen',
        matchPrefixes: ['/vertraege/mahnungen'],
      },
      { kind: 'item', label: 'Wartungs- & Prüfpläne', hint: 'Phase 5' },
    ],
  },
  {
    id: 'auftraege',
    label: 'Aufträge & Projekte',
    children: [
      { kind: 'item', label: 'Aufträge', to: '/auftraege', matchPrefixes: ['/auftraege'] },
      { kind: 'item', label: 'Projekte', to: '/projekte', matchPrefixes: ['/projekte'] },
      {
        kind: 'item',
        label: 'Reklamationen',
        to: '/reklamationen',
        matchPrefixes: ['/reklamationen'],
      },
      {
        kind: 'item',
        label: 'Bautagebuch',
        to: '/bautagebuch',
        matchPrefixes: ['/bautagebuch'],
      },
      { kind: 'item', label: 'Leistungsnachweise', hint: 'Phase 5 Strang C — ADR-0013' },
    ],
  },
  {
    id: 'planung',
    label: 'Planung',
    children: [
      { kind: 'item', label: 'Tageseinsatz & Schicht', hint: 'Planung-Phase' },
      {
        kind: 'item',
        label: 'Serienleistungen',
        to: '/serienleistungen',
        matchPrefixes: ['/serienleistungen'],
      },
      {
        kind: 'item',
        label: 'Kontrollgänge',
        to: '/kontrollgaenge',
        matchPrefixes: ['/kontrollgaenge'],
      },
    ],
  },
  {
    id: 'finanzen',
    label: 'Finanzen',
    children: [
      { kind: 'item', label: 'Angebote', to: '/angebote', matchPrefixes: ['/angebote'] },
      {
        kind: 'item',
        label: 'Ausgangsrechnungen',
        to: '/rechnungen',
        matchPrefixes: ['/rechnungen'],
      },
      {
        kind: 'item',
        label: 'Quartalsabrechnung',
        hint: 'BV — 3 Raten, ADR 0006',
      },
      {
        kind: 'item',
        label: 'Eingangsrechnungen',
        to: '/finanzen/eingangsrechnungen',
        matchPrefixes: ['/finanzen/eingangsrechnungen'],
      },
      { kind: 'item', label: 'Bank', to: '/finanzen/bank', matchPrefixes: ['/finanzen/bank'] },
      { kind: 'item', label: 'Mahnwesen & OP', hint: 'aktuell unter Verträge' },
      { kind: 'item', label: 'DATEV-Export', hint: 'DATEV-Phase' },
    ],
  },
  {
    id: 'personal',
    label: 'Personal',
    children: [
      {
        kind: 'item',
        label: 'Zeiterfassung',
        to: '/zeiterfassung',
        matchPrefixes: ['/zeiterfassung'],
      },
    ],
  },
  {
    id: 'betriebsmittel',
    label: 'Betriebsmittel',
    children: [
      { kind: 'item', label: 'Schlüssel', hint: 'Betriebsmittel-Phase' },
      { kind: 'item', label: 'Geräte & Lager', hint: 'Betriebsmittel-Phase' },
      { kind: 'item', label: 'Fuhrpark', hint: 'Betriebsmittel-Phase' },
    ],
  },
  {
    id: 'auswertungen',
    label: 'Auswertungen',
    children: [
      { kind: 'item', label: 'Gesamtumsatz', hint: 'Reporting-Phase' },
      { kind: 'item', label: 'Offene Angebote', hint: 'Reporting-Phase' },
      { kind: 'item', label: 'Umsatz pro Objekt', hint: 'Reporting-Phase' },
      { kind: 'item', label: 'Aufträge pro Mitarbeiter', hint: 'Reporting-Phase' },
    ],
  },
  {
    id: 'portale',
    label: 'Portale',
    children: [
      {
        kind: 'item',
        label: 'Portal-Benutzer',
        to: '/portal-users',
        matchPrefixes: ['/portal-users'],
      },
      {
        kind: 'item',
        label: 'Kundenportal',
        hint: 'portal.busch-gm.de — Inhalts-Pages: Phase 9',
      },
      {
        kind: 'item',
        label: 'Subunternehmer-Portal',
        hint: 'subportal.busch-gm.de — Inhalts-Pages: Phase 8',
      },
    ],
  },
  {
    id: 'einstellungen',
    label: 'Einstellungen',
    children: [
      { kind: 'item', label: 'Firma', hint: 'Settings-Phase' },
      { kind: 'item', label: 'Nummernkreise', hint: 'Settings-Phase' },
      { kind: 'item', label: 'Kontenrahmen', hint: 'DATEV-Phase' },
      { kind: 'item', label: 'Textbausteine', hint: 'VG-Polish' },
      { kind: 'item', label: 'Benutzer & Rechte', to: '/users', matchPrefixes: ['/users'] },
      {
        kind: 'item',
        label: 'Externe Anbindungen',
        hint: 'DATEV, M365, Banking',
      },
      { kind: 'item', label: 'GoBD-Doku', hint: 'GoBD-Phase' },
    ],
  },
];

/**
 * Berechnet ob ein Pfad als „aktiv" fuer ein Item gilt. Regel:
 * - Wenn `matchPrefixes` gesetzt: pathname muss mit einem der Prefixe starten.
 * - Sonst: exakter String-Match auf `to`.
 * Sub-Routen wie `/kunden/123` sollen also den Kunden-Eintrag aktiv machen.
 */
export function isItemActive(item: NavLeaf, pathname: string): boolean {
  if (!item.to) return false;
  const prefixes = item.matchPrefixes ?? [item.to];
  return prefixes.some((p) => pathname === p || pathname.startsWith(p + '/'));
}

/** Pruef rekursiv ob mindestens ein Item innerhalb einer Section/Subgroup aktiv ist. */
export function isAnyChildActive(children: readonly NavChild[], pathname: string): boolean {
  for (const c of children) {
    if (c.kind === 'item' && isItemActive(c, pathname)) return true;
    if (c.kind === 'group' && c.items.some((i) => isItemActive(i, pathname))) return true;
  }
  return false;
}
